/**
 * PaymentSourceModal.tsx
 *
 * "Choose payment method" — the chooser the Axiom order flow opens before
 * ChargePaymentModal. Patients often pay on the companion site first (the
 * payment already sits in FTP's payments table), so staff re-ordering pens or
 * vials can either charge a NEW payment (the existing ChargePaymentModal path,
 * via onChargeNew) or link one of the ELIGIBLE existing payments the server
 * offers — recent, non-void, unspent. SCRUM-1742: the amount no longer has
 * to match the order. Each row shows what the payment covered next to the
 * order total, so a shortfall or overage is visible before linking.
 *
 * Linking calls mark-paid with the paymentID; the server validates again and
 * releases an 'awaiting_payment' order to the vendor, so onLinked mirrors the
 * charge path's post-payment refresh.
 *
 * A combined pens + vials cart is two orders paid ONCE (SCRUM-2023): pass the
 * other order(s) as `siblingOrderIds` and the chosen payment is linked to each
 * of them. The server accepts that because they share a metadata.cartGroupId,
 * and keeps listing the payment for the sibling after the first link.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CreditCard } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import {
  getEligibleOrderPayments,
  markIntegrationOrderPaid,
  type EligibleOrderPayment,
} from '@/services/integrations.service';
import { getErrorMessage } from '@/services/apiClient';
import { useToast } from '@/hooks/useToast';
import { formatDate } from '@/lib/utils';

interface PaymentSourceModalProps {
  patientId: number;
  orderId: number;
  /** The other orders of the same combined cart, paid by the same payment. */
  siblingOrderIds?: number[];
  /**
   * What the whole cart comes to. The eligible list is read against the FIRST
   * order's charge only, so a cart compares payments with this instead.
   */
  cartTotal?: number;
  /** Staff chose to take a fresh payment — parent opens ChargePaymentModal. */
  onChargeNew: () => void;
  /** An existing payment was linked (order released) — parent refreshes + closes. */
  onLinked: () => void;
  onClose: () => void;
}

const usd = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

/** "Credit Card · Visa" / "Cash" — whichever of type/method exists. */
function payKind(p: EligibleOrderPayment): string {
  return [p.payType, p.payMethod].filter(Boolean).join(' · ');
}

/** "Ref REF-501 — memo…" truncated by the row's CSS, '' when neither exists. */
function payNote(p: EligibleOrderPayment): string {
  const parts: string[] = [];
  if (p.refNumber) parts.push(`Ref ${p.refNumber}`);
  if (p.memo) parts.push(p.memo);
  return parts.join(' — ');
}

export function PaymentSourceModal({
  patientId,
  orderId,
  siblingOrderIds = [],
  cartTotal,
  onChargeNew,
  onLinked,
  onClose,
}: PaymentSourceModalProps) {
  const toast = useToast();
  const [linkingId, setLinkingId] = useState<number | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['integration-order-eligible-payments', patientId, orderId],
    queryFn: () => getEligibleOrderPayments(patientId, orderId),
  });

  const payments = data?.payments ?? [];
  const alreadyCollected = data?.reason === 'no_pending_charge';
  const isCart = siblingOrderIds.length > 0;
  /** What a payment is compared with: the cart's total, else this order's charge. */
  const due = isCart && cartTotal != null ? cartTotal : (data?.amountDue ?? null);

  async function link(paymentID: number) {
    setLinkingId(paymentID);
    try {
      await markIntegrationOrderPaid(patientId, orderId, { paymentID });
    } catch (err) {
      toast.error(getErrorMessage(err));
      setLinkingId(null);
      return;
    }
    if (!isCart) {
      toast.success('Payment linked — order released');
      onLinked();
      return;
    }
    // The first order holds the payment now; each sibling is linked to the same
    // one. One failing never undoes the others — the toast names which order
    // still needs "Finish payment".
    const failed: string[] = [];
    for (const id of siblingOrderIds) {
      try {
        await markIntegrationOrderPaid(patientId, id, { paymentID });
      } catch (err) {
        failed.push(`#${id} (${getErrorMessage(err)})`);
      }
    }
    if (failed.length === 0) {
      toast.success('Payment linked to every order of the cart — orders released');
    } else {
      toast.error(`Payment linked to order #${orderId}, but not to ${failed.join(', ')} — use “Finish payment” on that order.`);
    }
    onLinked();
  }

  // The fee was already collected (e.g. paid on the companion site) — there is
  // nothing left to charge, so don't offer "Charge Payment now" at all.
  if (alreadyCollected) {
    return (
      <Modal isOpen onClose={onClose} title="Choose payment method" size="md">
        <div className="space-y-4">
          <p className="py-2 text-sm text-slate-600">
            The fee for this order is already collected — no further payment is needed.
          </p>
          <Button variant="secondary" fullWidth onClick={onClose}>
            Close
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen onClose={onClose} title="Choose payment method" size="md">
      <div className="space-y-4">
        {due != null && (
          <p className="text-sm text-slate-600">
            {isCart ? `Cart total (${siblingOrderIds.length + 1} orders, one payment)` : 'Order total'}: {usd(due)}
          </p>
        )}

        <Button
          variant="primary"
          fullWidth
          leftIcon={<CreditCard className="h-4 w-4" />}
          disabled={linkingId !== null}
          onClick={onChargeNew}
        >
          Charge Payment now
        </Button>

        <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-slate-400">
          <span className="h-px flex-1 bg-slate-200" />
          or use an existing payment
          <span className="h-px flex-1 bg-slate-200" />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner size="lg" />
          </div>
        ) : isError ? (
          <div className="space-y-2 py-2">
            <p className="text-sm text-slate-500">Couldn&apos;t load payments.</p>
            <Button variant="secondary" size="sm" onClick={() => void refetch()}>
              Retry
            </Button>
          </div>
        ) : payments.length === 0 ? (
          <p className="py-2 text-sm text-slate-500">
            No payments available to link — charge the patient instead.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {payments.map((p) => (
              <li key={p.paymentID} className="flex items-center gap-3 p-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 text-sm">
                    <span className="font-medium text-slate-900">{usd(p.amount)}</span>
                    <span className="text-slate-500">{formatDate(p.date)}</span>
                    {payKind(p) && <span className="text-slate-500">{payKind(p)}</span>}
                    {p.giftCardAmount > 0 && (
                      <span className="text-slate-500">· Gift card {usd(p.giftCardAmount)}</span>
                    )}
                    {/* SCRUM-1742: a payment no longer has to equal the order,
                        so the difference has to be visible BEFORE linking —
                        silently closing an order for less than it billed is the
                        one thing removing the rule could hide. */}
                    {due != null && Math.abs(p.amount - due) >= 0.01 && (
                      <span
                        className={p.amount < due ? 'text-amber-700' : 'text-slate-500'}
                      >
                        · {p.amount < due ? 'Short' : 'Over'} by{' '}
                        {usd(Math.abs(p.amount - due))}
                      </span>
                    )}
                  </div>
                  {payNote(p) && (
                    <p className="truncate text-xs text-slate-400" title={payNote(p)}>
                      {payNote(p)}
                    </p>
                  )}
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  loading={linkingId === p.paymentID}
                  disabled={linkingId !== null}
                  onClick={() => void link(p.paymentID)}
                >
                  Apply Payment
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
